/* eslint-disable react/prop-types */
import { FC, useState } from "react";
import { Bell, Smartphone } from "lucide-react";

interface Props {
	pushTitle: string;
	pushBody: string;
	appName?: string;
}

type PreviewMode = "collapsed" | "expanded";

const PushPreviewPanel: FC<Props> = ({
	pushTitle,
	pushBody,
	appName = "Mee"
}) => {
	// Collapsed = the banner / lock-screen view (iOS ~2 lines of body,
	// Android ~1). Expanded = what users see after long-pressing,
	// swiping down, or tapping — the full title and body with no
	// truncation. The two states differ slightly between iOS and
	// Android but broadly match across both, so a single toggle is
	// enough for editors to preview both cases.
	const [mode, setMode] = useState<PreviewMode>("collapsed");

	const displayTitle = pushTitle || "Notification title";
	const displayBody = pushBody || "Notification body text will appear here";

	const isCollapsed = mode === "collapsed";
	const titleClass = isCollapsed
		? "text-sm font-semibold leading-tight line-clamp-1 text-foreground"
		: "text-sm font-semibold leading-tight text-foreground break-words";
	const bodyClass = isCollapsed
		? "text-sm leading-snug mt-0.5 line-clamp-2 text-muted-foreground"
		: "text-sm leading-snug mt-0.5 text-muted-foreground whitespace-pre-wrap break-words";

	const renderToggleButton = (label: string, value: PreviewMode) => {
		const active = mode === value;
		return (
			<button
				type="button"
				onClick={() => setMode(value)}
				aria-pressed={active}
				className={[
					"flex-1 px-3 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer",
					active
						? "bg-white text-foreground shadow-sm"
						: "text-muted-foreground hover:text-foreground"
				].join(" ")}
			>
				{label}
			</button>
		);
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-2 max-w-[280px]">
				<p className="text-sm font-medium text-muted-foreground">Preview</p>
				<div
					role="group"
					aria-label="Notification preview state"
					className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-muted"
				>
					{renderToggleButton("Collapsed", "collapsed")}
					{renderToggleButton("Expanded", "expanded")}
				</div>
			</div>

			<div className="w-[280px]">
				<div className="bg-zinc-900 rounded-t-2xl px-4 py-2 flex items-center justify-between">
					<span className="text-white text-xs font-medium">9:41</span>
					<div className="flex items-center gap-1">
						<Smartphone className="h-3 w-3 text-white" />
					</div>
				</div>
				<div className="bg-zinc-900 px-3 pb-4 rounded-b-2xl">
					<div className="rounded-2xl p-3 shadow-sm bg-white/95 backdrop-blur-sm">
						<div className="flex items-center gap-2 mb-1.5">
							<div className="w-5 h-5 rounded-md bg-primary flex items-center justify-center">
								<Bell className="h-3 w-3 text-white" />
							</div>
							<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
								{appName}
							</span>
							<span className="text-xs ml-auto text-muted-foreground">now</span>
						</div>
						<p className={titleClass}>{displayTitle}</p>
						<p className={bodyClass}>{displayBody}</p>
					</div>
				</div>
			</div>

			<p
				aria-live="polite"
				className="text-xs text-muted-foreground mt-2 max-w-[280px] leading-snug"
			>
				{isCollapsed
					? "Banner / lock-screen view. Users see this at a glance — put the most important copy first."
					: "What users see when they tap, long-press, or pull down the notification. The full body is shown with no truncation."}
			</p>
		</div>
	);
};

export default PushPreviewPanel;
